"use client"

import Link from "next/link"
import { ArrowRight, CheckCircle2, Shield, PlayCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { AnimatedMockDashboard } from "./animated-mock-dashboard"
import { useAuth } from "@/hooks/use-auth"
import { useRouter } from "next/navigation"

export function LandingHero() {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
    const router = useRouter()

    const handleStartPracticing = () => {
        if (isAuthLoading) return
        if (!isAuthenticated) {
            router.push("/login?next=%2Fdashboard")
        } else {
            router.push("/dashboard")
        }
    }
    return (
        <section className="relative overflow-hidden pt-14 md:pt-16 lg:pt-20 pb-10 md:pb-14">
            {/* Background decorative elements */}
            <div className="absolute top-0 right-0 -mr-24 -mt-24 w-[28rem] h-[28rem] bg-primary/10 dark:bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 -ml-24 -mb-24 w-[24rem] h-[24rem] bg-primary/10 dark:bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>

            <div className="container px-4 sm:px-6 md:px-8 mx-auto">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">

                    {/* Left Column: Content */}
                    <div className="lg:col-span-5 max-w-xl relative z-10 text-center lg:text-left mx-auto lg:mx-0 animate-in fade-in slide-in-from-bottom-6 duration-1000 fill-mode-forwards">
                        <div className="space-y-7">
                            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-primary">
                                SATBuddy AI Prep Platform
                            </div>

                            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-[-0.035em] text-foreground leading-[1.03]">
                                Unlock Your <br className="hidden sm:block" />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/85 to-primary/55">
                                    Potential
                                </span>
                            </h1>

                            <p className="max-w-lg mx-auto lg:mx-0 text-base sm:text-lg leading-relaxed text-muted-foreground">
                                Structured SAT practice, AI feedback, and mentor guidance in one focused workflow built to improve your score with confidence.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-3 sm:pt-4 justify-center lg:justify-start px-2 sm:px-0">
                                <div className="w-full sm:w-auto">
                                    <Button 
                                        size="lg" 
                                        className="w-full h-14 px-8 text-base sm:text-lg font-semibold gap-2 rounded-full bg-primary hover:bg-primary/95 text-primary-foreground relative overflow-hidden transition-all duration-200 ease-out hover:-translate-y-0.5 active:translate-y-[2px] active:scale-[0.99] shadow-[0_12px_24px_-10px_hsl(var(--primary)/0.55),0_3px_0_hsl(var(--primary)/0.72)] active:shadow-[0_4px_10px_-8px_hsl(var(--primary)/0.45),0_1px_0_hsl(var(--primary)/0.72)]"
                                        onClick={handleStartPracticing}
                                        disabled={isAuthLoading}
                                    >
                                        Start Practicing <ArrowRight className="w-5 h-5" />
                                    </Button>
                                </div>
                                <Link href="/go-to-lms" className="w-full sm:w-auto">
                                    <Button variant="outline" size="lg" className="w-full h-14 px-8 text-base sm:text-lg font-semibold gap-2 rounded-full border-primary/30 hover:border-primary/40 hover:bg-primary/[0.06] dark:hover:bg-primary/[0.12] bg-background/65 backdrop-blur-sm relative overflow-hidden transition-all duration-200 ease-out hover:-translate-y-0.5 active:translate-y-[2px] active:scale-[0.99] shadow-[0_10px_22px_-14px_hsl(var(--foreground)/0.30),0_2px_0_hsl(var(--border))] active:shadow-[0_3px_8px_-8px_hsl(var(--foreground)/0.30),0_1px_0_hsl(var(--border))]">
                                        <PlayCircle className="w-5 h-5 text-primary" />
                                        My Courses
                                    </Button>
                                </Link>
                            </div>

                            {/* Minimal Footer Trust Indicator */}
                            <div className="pt-5 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-3 text-sm font-medium text-muted-foreground">
                                <div className="flex items-center gap-2.5">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    <span>Score Guarantee</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    <span>Real Exam Interface</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                    <span>AI-Driven Insights</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="hidden lg:flex lg:col-span-7 relative w-full max-w-xl lg:max-w-none mx-auto lg:mx-0 lg:ml-auto animate-in fade-in zoom-in-95 duration-1000 delay-300 justify-end" style={{ willChange: "transform, opacity" }}>
                        {/* Decorative background for the dashboard */}
                        <div className="absolute -inset-1 bg-gradient-to-tr from-primary/20 via-transparent to-accent/20 rounded-[2rem] blur-xl opacity-50 dark:opacity-30 mix-blend-multiply dark:mix-blend-screen pointer-events-none"></div>

                        <div className="w-[120%] lg:w-[130%] origin-right -mr-4 lg:-mr-12">
                            <AnimatedMockDashboard />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
